/**
 * Setup the environment for tabletop items
 */
import { Logger } from '../../../../../FrontendBundle/Resources/js/ofcs';
import DynamicCollisionView from '../collision/view/dynamicCollisionView';
import CreateConnectorToolView from '../svg/toolView/createConnectorToolView';
import CreateCutangleToolView from '../svg/toolView/createCutangleToolView';

/**
 * Setup the environment for tabletop items
 */
export default class TabletopItemEnvironment {
    /**
     * @param {AbstractItem} item
     * @param {Pimple} container
     */
    constructor(item, container) {
        this.item = item;

        this.container = container;
        this.validator = container.get('canvas.validator');
        this.layerManager = container.get('canvas.svg.layerManager');
        this.contextManager = container.get('canvas.contextManager');
        this.pathFactory = container.get('canvas.svg.pathFactory');
        this.itemFactory = container.get('canvas.itemFactory');
        this.guideManager = container.get('canvas.guideManager');
        this.insideTabletopManager = container.get('canvas.insideTabletopManager');
        this.createCutangleTool = this.container.get('canvas.tools.createCutangle');
        this.createConnectorTool = this.container.get('canvas.tools.createConnector');
        this.gridView = container.get('canvas.svg.gridView');
    }

    init() {
        Logger.debug('TabletopItemEnvironment.init():', this.item);

        this.context = new Ofcs.Editor.Context.Context(this.item, 'tabletop');

        this.itemLayer = this.layerManager.createItemLayer(this.item, {
            transform: this.pathFactory.getAbsoluteTransformString(this.item),
        });

        const marginColor = this.container.get('editor.color.marginColor');

        this.layerManager.createLayer('tabletop', null, false, this.itemLayer);
        this.layerManager.createLayer('corner-cut-interaction', null, false, this.itemLayer);
        this.layerManager.createLayer('grid', null, false, this.itemLayer);
        this.layerManager.createLayer('margins', { fill: marginColor, opacity: 0.35 }, false, this.itemLayer);
        this.layerManager.createLayer('shapes', { fill: 'url(#bagBackground)' }, false, this.itemLayer);
        this.layerManager.createLayer('images', null, false, this.itemLayer);
        this.layerManager.createLayer('cuts', null, false, this.itemLayer);
        this.layerManager.createLayer('ghost', null, false, this.itemLayer);
        this.layerManager.createLayer('corner-cut', null, false, this.itemLayer);

        // Move item layer when the foam item is moved inside a box
        this.item.on('change', (foam) => {
            if (foam.hasChangedProperty('position')) {
                this.itemLayer.getElement().attr('transform', this.pathFactory.getAbsoluteTransformString(foam, true));
            }
        });

        // This layer should be child of items-overlay layer
        const dynamicCollisionLayer = this.layerManager.createLayer('collisions', null, false, this.itemLayer);
        dynamicCollisionLayer.getElement().css('pointer-events', 'none');

        this.dynamicCollisionView = new DynamicCollisionView(
            this.item,
            dynamicCollisionLayer,
            this.container.get('canvas.selection'),
            this.pathFactory,
        );

        if (this.createCutangleTool) {
            this.createCutangleToolView = new CreateCutangleToolView(
                this.container.get('canvas'),
                this.createCutangleTool,
            );
        }
        if (this.createConnectorTool) {
            this.createConnectorToolView = new CreateConnectorToolView(
                this.container.get('canvas'),
                this.createConnectorTool,
            );
        }
    }

    activate() {
        Logger.debug('TabletopItemEnvironment.activate():', this.item);

        this.itemLayer.show();
        this.contextManager.addContext(this.context);
        this.validator.watchContainerItem(this.item);

        if (this.guideManager) this.gridView.setItem(this.item);

        // TODO: These should probably be done on context switch
        if (this.insideTabletopManager) this.insideTabletopManager.bindContainerItem(this.item);
        if (this.createCutangleTool) this.createCutangleTool.bindContainerItem(this.item);
        if (this.createConnectorTool) this.createConnectorTool.bindContainerItem(this.item);
    }

    deactivate() {
        Logger.debug('TabletopItemEnvironment.deactivate():', this.item);

        this.itemLayer.hide();
        this.contextManager.removeContext(this.context);
        this.validator.unwatchContainerItem(this.item);

        if (this.guideManager) this.gridView.unsetItem(this.item);

        // TODO: These should probably be done on context switch
        if (this.insideTabletopManager) this.insideTabletopManager.unbindContainerItem(this.item);
        if (this.createCutangleTool) this.createCutangleTool.unbindContainerItem(this.item);
        if (this.createConnectorTool) this.createConnectorTool.unbindContainerItem(this.item);
    }
}
